import Callout from "../scheduleCallout/callout";
// routeSelector.tsx
import React, { useState, useRef, useEffect } from "react";
import RouteData from "../../../interfaces/routes/routeData";
import "./routeSelector.scss";

interface IProps {
    onRouteChange: (data: RouteData) => void;
    selectedRoute: string;


}


const TABS = ["all", "bus", "rapid", "express", "rail"] as const;
type Tab = typeof TABS[number];

const routePlaceholder = "Routes";

const TAB_LABEL: Record<Tab, string> = {
    all: "All",
    bus: "Bus",
    rapid: "Rapid",
    express: "Express",
    rail: "Rail"
};

const TAB_DESCR: Record<Tab, string> = {
    all: "All CapMetro routes 1-990",
    bus: "CapMetro Local routes 1–499 and 600–699. Frequent-stop service with routes all over town, plus flyer routes with fewer stops and faster service.",
    rapid: "CapMetro Rapid routes 800–837 have fewer stops and higher speeds, serving major corridors with limited-stop service.",
    express: "CapMetro Express routes 900–999 connect outlying areas to the core network with minimal stops and extended hours.",
    rail: "CapMetro Rail route 550 - commuter service between Leander and downtown Austin."

};

// Choose initial tab from the current URL (priority: rapid > express > all > bus)
const deriveTabFromUrl = (url: string): Tab => {
    const u = (url || "").toLowerCase();
    if (u.includes("rapid")) return "rapid";
    if (u.includes("express")) return "express";
    if (u.includes("all")) return "all";
    if (u.includes("bus")) return "bus";
    return "all";
};

function RouteSelector({
    onRouteChange,
    selectedRoute
}: IProps) {
    const [routes, setRoutes] = useState<RouteData[]>([]);
    const [expanded, setExpanded] = useState(false);
    const [activeRoute, setActiveRoute] = useState<RouteData | undefined>();
    const [searchTerm, setSearchTerm] = useState("");
    const [tab, setTab] = useState<Tab>("all");
    //const [tab, setTab] = useState<Tab>("all"); // overridden on mount based on URL

    const inputRef = useRef<HTMLInputElement>(null);

    // Rail-mode flag from URL
    const isRailPage =
        typeof window !== "undefined" &&
        (window.location.href || "").toLowerCase().includes("rail");

    // Bus-mode flag from URL (hide Rail tab + remove 550 from list)
    const isBusPage =
        typeof window !== "undefined" &&
        (window.location.href || "").toLowerCase().includes("bus");

    // Route to preselect from query string (?route=935)
    const [routeFromQuery, setRouteFromQuery] = useState<number | undefined>(undefined);
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const v = params.get("route");
            if (v && /^\d+$/.test(v)) setRouteFromQuery(parseInt(v, 10));
        } catch { /* no-op for SSR */ }
    }, []);

    // your existing data-loading logic, unchanged
    useEffect(() => {
        const env = process.env.NODE_ENV;
        const api = "/webapi/atis/allroutes";
        if (env === "development" || window.location.origin.includes("capmetro-local")) {
            const demo = require("../demoData/allroutes.json");
            setRoutes(demo.Data);
        } else {
            fetch(api)
                .then(res => res.ok ? res.json() : Promise.reject("bad"))
                .then(js => setRoutes(js.Data))
                .catch(console.error);
        }
    }, []);



    // Preselect tab based on current page URL
    useEffect(() => {
        try {
            const initial = deriveTabFromUrl(window.location.href);
            setTab(initial);
        } catch {
            /* no-op for SSR or unusual environments */
        }
    }, []);

    // Keep search in sync with tab (ensures list is filtered by the selected tab)
    useEffect(() => {
        setSearchTerm("");
    }, [tab]);


    // if you want the dropdown to auto-focus the search input
    useEffect(() => {
        if (expanded) setTimeout(() => inputRef.current?.focus(), 100);
    }, [expanded]);

    // ── NEW: when Rail tab is clicked, select the only rail route by default ──
    useEffect(() => {
        if (tab === "rail") {
            const railRoutes = routes.filter((r) => r.RouteNumber === 550);
            if (railRoutes.length) {
                selectRoute(railRoutes[0]);
            }
        }
    }, [tab, routes]);

    // Tab-based filtering by route number
    const filteredByTab = routes.filter(r => {
        const n = r.RouteNumber;
        switch (tab) {
            case "all": return true;
            case "bus": return (n >= 1 && n <= 499) || (n >= 600 && n <= 699);
            case "rapid": return n >= 800 && n <= 837;
            case "express": return n >= 900 && n <= 999;
            case "rail": return n >= 550 && n <= 550;
        }
    });

    // Then search-term filtering
    const visibleRoutes = filteredByTab
        .filter(r =>
            `${r.RouteNumber} - ${r.DisplayName}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        )
        // On bus pages, remove rail route 550 from the dropdown
        .filter(r => !(isBusPage && r.RouteNumber === 550));

    const toggleDropdown = () => {
        setExpanded(x => !x);
        setSearchTerm("");
    };

    const selectRoute = (r: RouteData) => {
        setActiveRoute(r);
        setExpanded(false);
        onRouteChange(r);
    };



    // Preselect from ?route=... if present; otherwise rail → 550
    useEffect(() => {
        if (!routes.length) return;
        // 1) Explicit query param wins
        if (routeFromQuery !== undefined) {
            const match = routes.find(r => r.RouteNumber === routeFromQuery);
            if (match && activeRoute?.RouteNumber !== routeFromQuery) {
                selectRoute(match);
                return;
            }
        }
        // 2) Fallback for rail pages
        if (isRailPage) {
            const r550 = routes.find(r => r.RouteNumber === 550);
            if (r550 && activeRoute?.RouteNumber !== 550) selectRoute(r550);
        }
    }, [routes, routeFromQuery, isRailPage, activeRoute]);


    const placeholder = activeRoute
        // if your DisplayName already starts with "123 -", just show it
        ? (
            activeRoute.DisplayName
            //activeRoute.DisplayName.startsWith(`${activeRoute.RouteNumber} -`)
            //    ? activeRoute.DisplayName
            //    : `${activeRoute.RouteNumber} - ${activeRoute.DisplayName}`
        )
        : `Select or type in a ${tab === "all" ? "route" : TAB_LABEL[tab].toLowerCase() + " route"}…`;

    return (
        <div className="route-selector">
            {/* ── HEADER: Tabs + Title/Description + Actions ── */}
            <div className="route-selector__header">
                <div className="route-selector__controls">

                    {!isRailPage && (
                        <nav className="route-selector__tabs">
                            {(isBusPage ? TABS.filter(t => t !== "rail") : TABS).map(t => (


                                <button key={t} data-tab={t}
                                    className={`route-selector__tab${tab === t ? " active" : ""}`}
                                    onClick={() => setTab(t)} >
                                    {TAB_LABEL[t]}
                                </button>
                            ))}
                        </nav>
                    )}

                    {/*<div className="rail-link-wrap"> <a className="btn btn--neutral rail" href="/rail">*/}
                    {/*     <i className="icon-rail" /> <span>Ride Rail</span>*/}
                    {/*     </a>*/}

                    {/*</div>*/}



                    {!isRailPage && <span className="divider divider--desktop"></span>}

                    {/*Start journey tool mobile callout*/}
                    {!isRailPage && (
                        <div className="buttons-panel schedule-map__rail">
                            <Callout type="desktop" target="schedule-callout-primary" />
                            <Callout type="desktop" target="schedule-callout-secondary" />
                        </div>
                    )}
                    {/*End of journey tool mobile callout*/}


                </div>

                {/*End of controls*/}
                {!isRailPage && (
                 
                  <p className="route-selector__actions my-xl">{TAB_DESCR[tab]}</p>
                  
                )}

            </div>

            {/* ── BODY: Dropdown/Search ── */}
            {!isRailPage && (
                <div className="route-selector__body">
                    <button
                        type="button"
                        className="route-selector__toggle"
                        aria-expanded={expanded}
                        onClick={toggleDropdown}
                    >
                        {placeholder}
                        <span className="route-selector__arrow" />
                    </button>

                    {expanded && (
                        <div className="route-selector__dropdown">
                            <input
                                ref={inputRef}
                                type="text"
                                className="route-selector__search"
                                placeholder={`Type to search:  ${TAB_LABEL[tab]} ${routePlaceholder}`}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)} />
                            <ul className="route-selector__list">
                                {visibleRoutes.map(r => (
                                    <li key={r.RouteNumber}>
                                        <button
                                            className={`route-selector__option${activeRoute?.RouteNumber === r.RouteNumber ? " active" : ""}`}
                                            onClick={() => selectRoute(r)}
                                        >
                                            {r.DisplayName.startsWith(`${r.RouteNumber} -`)
                                                ? r.DisplayName
                                                : `${r.RouteNumber} - ${r.DisplayName}`}
                                        </button>
                                    </li>
                                ))}
                                {visibleRoutes.length === 0 && (
                                    <li className="route-selector__no-results">No results found</li>
                                )}
                            </ul>
                        </div>
                    )
                    }
                </div >
            )}
        </div>
    );
}

export default RouteSelector;
